<script setup>
/**
 * Six screens between an owner and a paid WhatsApp send. THE PLAN, §4 P10.
 *
 * THE STEP IS IN THE URL. `…/campaigns/broadcasts/new?step=audience` is the step, not a
 * ref called `currentStep`, and every consequence of that is deliberate:
 *
 *   • the browser's own Back button walks the sequence backwards, so the one control every
 *     person already knows how to use does the obvious thing instead of throwing away
 *     twenty minutes of work;
 *   • the rail's completed steps are real `router-link`s, so a step can be middle-clicked,
 *     bookmarked and reopened;
 *   • "which step am I on" has exactly one authority, and it is the one thing on screen
 *     that is already visible to the user.
 *
 * A STEP YOU HAVE NOT EARNED IS NOT A STEP YOU CAN BE ON. `resolveStep`
 * (`helpers/broadcast-compose.js`) takes the step the URL asked for and hands back the
 * first one that is not yet complete, and this page `router.replace`s onto that whenever
 * they differ. So `?step=check` typed into the address bar renders "Name it", not a Send
 * button — which is the whole difference between a sequence anchored on routes and DOM
 * state with a URL painted on it. `replace`, not `push`, so a bounce does not become a
 * history entry the Back button has to fight through.
 *
 * WHAT SURVIVES A RELOAD: nothing, and that is correct. The draft lives in this component,
 * and this component is inside `CampaignsPageRouteView`'s `<keep-alive>`, so it survives a
 * trip to the list and back — but a browser reload starts again at step one. A half-built
 * broadcast restored from storage, with a count taken against an audience the owner can no
 * longer see, is a worse thing to own than a retyped name.
 *
 * THE TWO SPEND GATES ARE THE SERVER'S. This page keeps its own `isStale` flag, which is
 * the same question the server's first gate asks — but only so that being refused is never
 * how the owner FINDS OUT that going back and editing invalidated her count. The server
 * decides; this only agrees early, and prints the server's sentence verbatim when it does
 * not.
 */
import { computed, onActivated, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import AudienceRow from 'dashboard/routes/dashboard/wasloai/components/broadcast/AudienceRow.vue';
import BindingRow from 'dashboard/routes/dashboard/wasloai/components/broadcast/BindingRow.vue';
import ComposeStepRail from 'dashboard/routes/dashboard/wasloai/components/broadcast/ComposeStepRail.vue';
import SendCheck from 'dashboard/routes/dashboard/wasloai/components/broadcast/SendCheck.vue';
import NotSetUp from 'dashboard/routes/dashboard/wasloai/components/broadcast/NotSetUp.vue';
import TemplateChoice from 'dashboard/routes/dashboard/wasloai/components/broadcast/TemplateChoice.vue';
import WasloInput from 'dashboard/routes/dashboard/wasloai/components/WasloInput.vue';
import WasloSelect from 'dashboard/routes/dashboard/wasloai/components/WasloSelect.vue';
import WasloPageLayout from 'dashboard/routes/dashboard/wasloai/components/WasloPageLayout.vue';
import { useWasloI18n } from 'dashboard/routes/dashboard/wasloai/i18n.js';
import { useWasloApi } from 'dashboard/routes/dashboard/wasloai/composables/useWasloApi.js';
import { useWasloAccountId } from 'dashboard/routes/dashboard/wasloai/composables/useWasloAccountId.js';
import {
  audienceRow,
  bindingsForTemplate,
  blankDraft,
  draftToWire,
  isStepComplete,
  renderTemplateBody,
  resolveStep,
  stepAfter,
  stepBefore,
  stepsFor,
} from 'dashboard/routes/dashboard/wasloai/helpers/broadcast-compose.js';
import {
  isBroadcastNotSetUp,
  wasloApi,
} from 'dashboard/routes/dashboard/wasloai/api/client.js';

const { t } = useWasloI18n();
const route = useRoute();
const router = useRouter();
const accountId = useWasloAccountId();

const draft = ref(blankDraft());

// ── what the sequence is chosen FROM ───────────────────────────────────────────────
const channels = useWasloApi(
  signal => wasloApi.getBroadcastChannels(accountId.value, signal),
  { isEmpty: () => false }
);
const audienceFields = useWasloApi(
  signal => wasloApi.getAudienceFields(accountId.value, signal),
  { isEmpty: () => false }
);
// `immediate: false` — there is nothing to ask for until a channel is chosen, and
// templates are approved PER SENDER, so a list fetched without one is not a smaller answer
// but a wrong one.
const templates = useWasloApi(
  signal =>
    wasloApi.getMessageTemplates(
      accountId.value,
      draft.value.channelId,
      signal
    ),
  { immediate: false, isEmpty: () => false }
);

watch(
  () => draft.value.channelId,
  channelId => {
    if (channelId) templates.reload();
  },
  { immediate: true }
);

const templateRows = computed(() => templates.data.value ?? []);
const chosenTemplate = computed(
  () =>
    templateRows.value.find(
      row =>
        row.name === draft.value.templateName &&
        row.language === draft.value.templateLanguage
    ) ?? null
);

const customAttributes = computed(() =>
  (audienceFields.data.value ?? []).filter(field => field.custom)
);

// ── the step, and the guard in front of it ─────────────────────────────────────────
const requestedStep = computed(() => String(route.query.step ?? 'name'));
const steps = computed(() => stepsFor(chosenTemplate.value));

// The guard is suspended while a channel's templates are still being read: until they
// arrive `chosenTemplate` is null, "fill in the blanks" cannot be complete, and a deep link
// past it would bounce to a step that is about to become irrelevant.
const isSettling = computed(
  () => Boolean(draft.value.channelId) && templates.isFetching.value
);

const step = computed(() =>
  isSettling.value
    ? requestedStep.value
    : resolveStep(requestedStep.value, draft.value, chosenTemplate.value)
);

const linkFor = target => ({
  path: frontendURL(`accounts/${accountId.value}/campaigns/broadcasts/new`),
  query: { step: target },
});

const goToStep = (target, replace = false) => {
  if (accountId.value === null) return;
  const to = linkFor(target);
  if (replace) router.replace(to);
  else router.push(to);
};

/**
 * True from the instant a send is confirmed until the owner comes back for another one.
 *
 * It exists to switch the guard below OFF, and that is not a nicety: the send navigates to
 * the broadcast's detail route, which drops `?step=` — so a guard still watching would read
 * "the URL asks for `name`", resolve `name`, and `router.replace` the owner straight back
 * into the compose sequence she just finished. The page is kept alive, so it is still
 * running when that happens.
 */
const hasLaunched = ref(false);

// Whenever the URL asks for more than the draft has earned, correct the URL — never the
// other way round, so the address bar and the screen cannot disagree.
watch(
  [step, requestedStep, isSettling],
  ([resolved, requested, settling]) => {
    if (hasLaunched.value || settling) return;
    if (resolved !== requested) goToStep(resolved, true);
  },
  { immediate: true }
);

/**
 * The steps the rail may link to: every completed one, plus the first that is not.
 *
 * Sliced rather than looped, because it is exactly a prefix — the guard admits a step only
 * when everything before it is complete, so "reachable" IS "up to and including the first
 * incomplete one". Deriving it any other way is a second rule that can disagree with the
 * guard.
 */
const reachable = computed(() => {
  const firstIncomplete = steps.value.findIndex(
    candidate => !isStepComplete(candidate, draft.value, chosenTemplate.value)
  );
  return firstIncomplete === -1
    ? [...steps.value]
    : steps.value.slice(0, firstIncomplete + 1);
});

const position = computed(() =>
  t('WASLO.BROADCASTS.COMPOSE.POSITION', {
    number: steps.value.indexOf(step.value) + 1,
    total: steps.value.length,
  })
);

const listUrl = computed(() =>
  accountId.value === null
    ? null
    : frontendURL(`accounts/${accountId.value}/campaigns/broadcasts`)
);

const nextStep = computed(() => stepAfter(step.value, chosenTemplate.value));
const previousStep = computed(() =>
  stepBefore(step.value, chosenTemplate.value)
);
const canAdvance = computed(() =>
  isStepComplete(step.value, draft.value, chosenTemplate.value)
);

const NEEDS_KEY = {
  name: 'NAME',
  channel: 'CHANNEL',
  message: 'MESSAGE',
  fill: 'FILL',
  audience: 'AUDIENCE',
};

/**
 * The sentence that stands where Next would be, and the reason there is no disabled Next.
 *
 * `Button`'s `disabled:opacity-50` composites the whole element over the page, so a solid
 * accent fill and its white label fade toward the background TOGETHER and meet in the
 * middle at about 2:1 — measured, in both themes, by two blind critics on the Personality
 * save bar. That bar's answer was to stop asking a primary to represent "nothing to do",
 * and this is the same answer: one sentence naming what is missing is both legible and
 * more useful than a dead control.
 */
const whatIsMissing = computed(() => {
  if (canAdvance.value || !nextStep.value) return '';
  const key = NEEDS_KEY[step.value];
  return key ? t(`WASLO.BROADCASTS.COMPOSE.NEEDS.${key}`) : '';
});

// ── editing ────────────────────────────────────────────────────────────────────────
const fieldErrors = ref({});
const formError = ref('');

/** Any edit at all invalidates a count that has already been taken. */
const clearErrors = () => {
  fieldErrors.value = {};
  formError.value = '';
};

const chooseChannel = value => {
  const channelId = Number(value);
  if (channelId === draft.value.channelId) return;
  clearErrors();
  // A different sender has a different approved list, so the message and everything
  // downstream of it stop being answers to anything.
  draft.value = {
    ...draft.value,
    channelId,
    templateName: '',
    templateLanguage: '',
    bindings: {},
  };
};

const chooseTemplate = template => {
  clearErrors();
  draft.value = {
    ...draft.value,
    templateName: template.name,
    templateLanguage: template.language,
    // Seeded from what is already held, so re-picking the same message on the way back does
    // not throw away blanks the owner has filled in.
    bindings: bindingsForTemplate(template, draft.value.bindings),
  };
  // Deliberately NO auto-advance. Choosing is not the same as being finished choosing —
  // an owner comparing three approved messages would have the page jump out from under her
  // on the first one she pressed. The docked Next is the one way forward, on every step.
};

const setBinding = (variable, binding) => {
  clearErrors();
  draft.value = {
    ...draft.value,
    bindings: { ...draft.value.bindings, [variable]: binding },
  };
};

const setAudienceRow = (id, row) => {
  clearErrors();
  draft.value = {
    ...draft.value,
    audience: draft.value.audience.map(existing =>
      existing.id === id ? { ...row, id } : existing
    ),
  };
};

const addAudienceRow = () => {
  clearErrors();
  draft.value = {
    ...draft.value,
    audience: [...draft.value.audience, audienceRow()],
  };
};

const removeAudienceRow = id => {
  clearErrors();
  const remaining = draft.value.audience.filter(row => row.id !== id);
  // Never zero rows: an audience step with nothing on it reads as a step with nothing to
  // do, and this is the one screen where "everyone" must never be the accidental answer.
  draft.value = {
    ...draft.value,
    audience: remaining.length ? remaining : [audienceRow()],
  };
};

// ── counting, and sending ──────────────────────────────────────────────────────────
const preview = ref(null);
/** The exact wire body the count on screen was taken against. */
const countedWire = ref('');
const isCounting = ref(false);
const isSending = ref(false);

const wire = computed(() => draftToWire(draft.value));

/**
 * The client half of the server's first spend gate.
 *
 * Compared as the SERIALISED WIRE BODY, not the draft: row ids, a blank condition that was
 * added and removed, and a trailing space in the name are all things that change the draft
 * and change nothing about what would be sent. The server computes its fingerprint from the
 * parsed state for the same reason, so the two agree about what "changed" means.
 */
const isStale = computed(
  () =>
    Boolean(preview.value) && JSON.stringify(wire.value) !== countedWire.value
);

const STEP_FOR_FIELD = {
  title: 'name',
  channelId: 'channel',
  templateName: 'message',
  bindings: 'fill',
  audience: 'audience',
};

/**
 * A refusal, put where the owner can act on it.
 *
 * `validation_failed` always carries `fields` keyed by the request-body key
 * (`http-errors.ts`), which is what lets this send her back to the step that caused it
 * instead of printing a sentence about "the form". Everything else — a 409 because the
 * message was withdrawn, a dead connection, the two spend gates — is one sentence at form
 * level, in the SERVER's words, because that copy exists exactly once and must not be
 * paraphrased here.
 */
const handleFailure = error => {
  if (error?.fields) {
    fieldErrors.value = error.fields;
    const offending = Object.keys(error.fields)
      .map(key => STEP_FOR_FIELD[key])
      .filter(Boolean);
    const earliest = steps.value.find(candidate =>
      offending.includes(candidate)
    );
    if (earliest && earliest !== step.value) goToStep(earliest);
    return;
  }
  formError.value =
    error?.message || t('WASLO.BROADCASTS.COMPOSE.CHECK.FAILED');
};

/** The `bindings` sentence belongs under the blank it names, and under no other. */
const errorForBlank = variable => {
  const message = fieldErrors.value.bindings ?? '';
  return message.includes(`{{${variable}}}`) ? message : '';
};

const count = async () => {
  if (isCounting.value) return;
  isCounting.value = true;
  clearErrors();
  try {
    const body = wire.value;
    const result = await wasloApi.previewBroadcast(accountId.value, body);
    preview.value = result;
    countedWire.value = JSON.stringify(body);
  } catch (error) {
    preview.value = null;
    countedWire.value = '';
    handleFailure(error);
  } finally {
    isCounting.value = false;
  }
};

const send = async acknowledged => {
  if (isSending.value || !preview.value) return;
  isSending.value = true;
  formError.value = '';
  try {
    const launched = await wasloApi.launchBroadcast(accountId.value, {
      ...wire.value,
      // The three echoes the server's two gates are checked against. See `api/client.js`.
      previewFingerprint: preview.value.fingerprint,
      previewExcluded: preview.value.willBeSkipped,
      acknowledged,
    });
    // NO TOAST. The confirmation is the destination: a detail page reading "Sending now"
    // over a live progress breakdown says more than a sentence that is gone in four
    // seconds, and a toast claiming a send while the page behind it still showed a Send
    // button is exactly the reading this sequence is built to prevent.
    //
    // The sequence is over, and the draft is deliberately NOT cleared here. Clearing it
    // now would re-run the step guard against an empty draft on the way out and bounce the
    // owner back into compose; it is cleared on the way IN instead (`onActivated`), which
    // is the only moment a stale draft could actually be seen.
    hasLaunched.value = true;
    if (accountId.value !== null) {
      await router.replace(
        frontendURL(
          `accounts/${accountId.value}/campaigns/broadcasts/${launched.id}`
        )
      );
    }
  } catch (error) {
    handleFailure(error);
  } finally {
    isSending.value = false;
  }
};

// ── rendering the message back ─────────────────────────────────────────────────────
const FIELD_KEY = {
  name: 'NAME',
  phoneNumber: 'PHONENUMBER',
  email: 'EMAIL',
};

const labelForBinding = binding => {
  if (!binding) return '?';
  if (binding.src === 'literal') return binding.value;
  if (binding.src === 'attr') {
    const field = customAttributes.value.find(
      candidate => candidate.key === binding.key
    );
    return field ? field.label : binding.key;
  }
  return t(
    `WASLO.BROADCASTS.COMPOSE.FILL.FIELDS.${FIELD_KEY[binding.key] ?? 'NAME'}`
  );
};

const renderedBody = computed(() =>
  chosenTemplate.value
    ? renderTemplateBody(
        chosenTemplate.value.body,
        draft.value.bindings,
        labelForBinding
      )
    : ''
);

const channelOptions = computed(() =>
  (channels.data.value ?? []).map(channel => ({
    value: String(channel.id),
    label: channel.phoneNumber
      ? `${channel.name} · ${channel.phoneNumber}`
      : `${channel.name} · ${t('WASLO.BROADCASTS.COMPOSE.CHANNEL.NO_NUMBER')}`,
    // A channel with no assistant on it cannot broadcast at all — offered and refused,
    // rather than absent, so an owner is not left hunting for a number she can see in
    // Chatwoot.
    disabled: !channel.usable,
  }))
);

const isFetching = computed(
  () => channels.isFetching.value || audienceFields.isFetching.value
);

/**
 * P-5.5: sending was never set up here. A state with one button, not an error. BOTH
 * catalogues need the sending credentials and both answer the same 409, so either one is
 * the verdict, and neither of them is an error: one left in `errorMessage` wins over the
 * empty state in the layout and puts "We couldn't load this" with a Retry back on screen.
 */
const notSetUp = computed(
  () =>
    isBroadcastNotSetUp(channels.error.value) ||
    isBroadcastNotSetUp(audienceFields.error.value)
);

// The two catalogues are the sequence's raw material; without them there is no sequence.
// Not set up is checked first: past it, neither error can be the 409.
const errorMessage = computed(() =>
  !notSetUp.value && (channels.error.value || audienceFields.error.value)
    ? t('WASLO.COMMON.ERROR_BODY')
    : ''
);

const reload = () => {
  channels.reload();
  audienceFields.reload();
  if (draft.value.channelId) templates.reload();
};

// Kept alive by `CampaignsPageRouteView`. Two things happen on the way back in:
//
//   • a sequence that ENDED in a send starts over. This component is never unmounted, so
//     without this "New broadcast" would open on the draft that already went out — with a
//     count, an acknowledgement and a live Send button over it.
//   • the catalogues are re-read: a number provisioned or a message approved in the
//     meantime should be there.
onActivated(() => {
  if (hasLaunched.value) {
    hasLaunched.value = false;
    draft.value = blankDraft();
    preview.value = null;
    countedWire.value = '';
    clearErrors();
  }
  if (channels.hasLoaded.value) reload();
});
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.BROADCASTS.COMPOSE.TITLE')"
    :back-url="listUrl"
    :back-label="t('WASLO.BROADCASTS.COMPOSE.BACK')"
    :is-fetching="isFetching"
    :error-message="errorMessage"
    :is-empty="notSetUp"
    @retry="reload"
  >
    <template #body>
      <div class="flex flex-col gap-8 pb-10">
        <div class="flex flex-col gap-3">
          <ComposeStepRail
            :steps="steps"
            :current="step"
            :reachable="reachable"
            :link-for="linkFor"
          />
          <p class="mb-0 text-xs text-n-slate-10">{{ position }}</p>
        </div>

        <!-- 1 · NAME IT -->
        <section v-if="step === 'name'" class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.BROADCASTS.COMPOSE.NAME.HEADING')"
            :description="t('WASLO.BROADCASTS.COMPOSE.NAME.DESCRIPTION')"
          />
          <WasloInput
            :model-value="draft.title"
            :label="t('WASLO.BROADCASTS.COMPOSE.NAME.LABEL')"
            :placeholder="t('WASLO.BROADCASTS.COMPOSE.NAME.PLACEHOLDER')"
            :message="fieldErrors.title || ''"
            :message-type="fieldErrors.title ? 'error' : 'info'"
            data-waslo-broadcast-name
            @update:model-value="
              value => {
                clearErrors();
                draft = { ...draft, title: value };
              }
            "
          />
        </section>

        <!-- 2 · THE NUMBER IT COMES FROM -->
        <section v-else-if="step === 'channel'" class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.BROADCASTS.COMPOSE.CHANNEL.HEADING')"
            :description="t('WASLO.BROADCASTS.COMPOSE.CHANNEL.DESCRIPTION')"
          />
          <Banner v-if="channelOptions.length === 0" color="slate">
            {{ t('WASLO.BROADCASTS.COMPOSE.CHANNEL.NONE') }}
          </Banner>
          <WasloSelect
            v-else
            :model-value="draft.channelId ? String(draft.channelId) : ''"
            :options="channelOptions"
            :label="t('WASLO.BROADCASTS.COMPOSE.STEPS.CHANNEL')"
            :placeholder="t('WASLO.BROADCASTS.COMPOSE.CHANNEL.PLACEHOLDER')"
            :message="fieldErrors.channelId || ''"
            :message-type="fieldErrors.channelId ? 'error' : 'info'"
            data-waslo-broadcast-channel
            @update:model-value="chooseChannel"
          />
        </section>

        <!-- 3 · THE APPROVED MESSAGE -->
        <section v-else-if="step === 'message'" class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.BROADCASTS.COMPOSE.MESSAGE.HEADING')"
            :description="t('WASLO.BROADCASTS.COMPOSE.MESSAGE.DESCRIPTION')"
          />
          <Banner v-if="templates.error.value" color="slate">
            {{ t('WASLO.BROADCASTS.COMPOSE.MESSAGE.UNAVAILABLE') }}
          </Banner>
          <Banner v-else-if="templateRows.length === 0" color="slate">
            {{ t('WASLO.BROADCASTS.COMPOSE.MESSAGE.NONE') }}
          </Banner>
          <div v-else class="grid gap-3" data-waslo-template-list>
            <TemplateChoice
              v-for="template in templateRows"
              :key="`${template.name}:${template.language}`"
              :template="template"
              :is-chosen="
                template.name === draft.templateName &&
                template.language === draft.templateLanguage
              "
              @choose="chooseTemplate"
            />
          </div>
          <p v-if="fieldErrors.templateName" class="mb-0 text-sm text-n-ruby-9">
            {{ fieldErrors.templateName }}
          </p>
        </section>

        <!-- 4 · FILL IN THE BLANKS -->
        <section v-else-if="step === 'fill'" class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.BROADCASTS.COMPOSE.FILL.HEADING')"
            :description="t('WASLO.BROADCASTS.COMPOSE.FILL.DESCRIPTION')"
          />
          <!--
            The server sends ONE sentence for `bindings`, and it names the blank it is
            about ("Enter the fixed text for {{2}}."). Showing it under every row would mark
            four correct blanks as wrong, so `errorForBlank` matches on the name and puts it
            under the one that is.
          -->
          <div class="grid gap-3" data-waslo-binding-list>
            <BindingRow
              v-for="variable in chosenTemplate ? chosenTemplate.variables : []"
              :key="variable"
              :variable="variable"
              :binding="
                draft.bindings[variable] ?? { src: 'field', key: 'name' }
              "
              :attributes="customAttributes"
              :error="errorForBlank(variable)"
              @update="binding => setBinding(variable, binding)"
            />
          </div>
        </section>

        <!-- 5 · WHO GETS IT -->
        <section v-else-if="step === 'audience'" class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.HEADING')"
            :description="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.DESCRIPTION')"
          />
          <Banner v-if="audienceFields.error.value" color="slate">
            {{ t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.UNAVAILABLE') }}
          </Banner>
          <div class="flex flex-col gap-3" data-waslo-audience-list>
            <AudienceRow
              v-for="row in draft.audience"
              :key="row.id"
              :row="row"
              :fields="audienceFields.data.value ?? []"
              :can-remove="draft.audience.length > 1"
              @update="updated => setAudienceRow(row.id, updated)"
              @remove="removeAudienceRow(row.id)"
            />
          </div>
          <Button
            :label="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.ADD')"
            icon="i-lucide-plus"
            variant="faded"
            color="slate"
            size="sm"
            class="self-start"
            data-waslo-audience-add
            @click="addAudienceRow"
          />
          <p v-if="fieldErrors.audience" class="mb-0 text-sm text-n-ruby-9">
            {{ fieldErrors.audience }}
          </p>
        </section>

        <!-- 6 · CHECK AND SEND -->
        <section v-else class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.BROADCASTS.COMPOSE.CHECK.HEADING')"
            :description="t('WASLO.BROADCASTS.COMPOSE.CHECK.DESCRIPTION')"
          />
          <SendCheck
            :rendered-body="renderedBody"
            :preview="preview"
            :is-counting="isCounting"
            :is-sending="isSending"
            :is-stale="isStale"
            :error-message="formError"
            @count="count"
            @send="send"
          />
        </section>
      </div>
    </template>

    <!--
      Back and Next are DOCKED, outside the scrollport, for the same reason the Personality
      save bar is: a commit point you have to scroll to find is not a commit point. There is
      no Next on the last step — the send lives beside the count it is about, where the
      number and the acknowledgement are still on screen.
    -->
    <template #footer>
      <div
        class="flex flex-wrap items-center justify-between py-4 gap-x-6 gap-y-3"
        data-waslo-compose-footer
      >
        <Button
          v-if="previousStep"
          :label="t('WASLO.BROADCASTS.COMPOSE.PREVIOUS')"
          variant="faded"
          color="slate"
          data-waslo-compose-back
          @click="goToStep(previousStep)"
        />
        <router-link
          v-else-if="listUrl"
          :to="listUrl"
          class="inline-flex items-center h-10 gap-1 px-3 text-sm font-medium rounded-lg text-n-slate-11 hover:text-n-slate-12"
        >
          <span
            class="size-4 i-lucide-chevron-left rtl:rotate-180"
            aria-hidden="true"
          />
          {{ t('WASLO.BROADCASTS.COMPOSE.LEAVE') }}
        </router-link>

        <!-- Next at full strength, or the sentence that replaces it. Never a greyed-out
             primary — see `whatIsMissing`. -->
        <Button
          v-if="nextStep && canAdvance"
          :label="t('WASLO.BROADCASTS.COMPOSE.NEXT')"
          variant="solid"
          color="blue"
          data-waslo-compose-next
          @click="goToStep(nextStep)"
        />
        <p
          v-else-if="whatIsMissing"
          class="mb-0 text-sm text-n-slate-11"
          data-waslo-compose-needs
        >
          {{ whatIsMissing }}
        </p>
      </div>
    </template>

    <template #emptyState>
      <NotSetUp :account-id="accountId" data-waslo-broadcasts-empty />
    </template>
  </WasloPageLayout>
</template>
